import { useEffect, useRef, useState, type RefObject } from 'react';
import type { HandLandmarker } from '@mediapipe/tasks-vision';
import type { HandTrackingStatus } from './types';

// Served from the app itself; scripts/copy-mediapipe-assets.mjs copies these
// from @mediapipe/tasks-vision into public/ at start/build time. The bundle
// is loaded via a runtime URL import because webpack can't bundle it (it
// contains dynamic dependency expressions that fail the CI build).
const TASKS_VISION_BUNDLE_URL = '/mediapipe/vision_bundle.mjs';
const WASM_BASE_URL = '/mediapipe/wasm';
const HAND_LANDMARKER_MODEL_URL =
  'https://storage.googleapis.com/mediapipe-models/hand_landmarker/hand_landmarker/float16/1/hand_landmarker.task';

type TasksVisionModule = typeof import('@mediapipe/tasks-vision');

async function loadTasksVision(): Promise<TasksVisionModule> {
  return import(
    /* webpackIgnore: true */
    TASKS_VISION_BUNDLE_URL
  ) as Promise<TasksVisionModule>;
}

const WRIST = 0;
const MIDDLE_FINGER_MCP = 9;
const SMOOTHING = 0.35;

export interface UseHandTrackingResult {
  status: HandTrackingStatus;
  videoRef: RefObject<HTMLVideoElement>;
  handYRef: RefObject<number | null>;
  errorMessage: string | null;
}

export function useHandTracking(enabled: boolean): UseHandTrackingResult {
  const [status, setStatus] = useState<HandTrackingStatus>('idle');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const handYRef = useRef<number | null>(null);

  useEffect(() => {
    if (!enabled) {
      setStatus('idle');
      setErrorMessage(null);
      handYRef.current = null;
      return undefined;
    }

    if (!navigator.mediaDevices?.getUserMedia) {
      setStatus('unsupported');
      setErrorMessage(
        "This browser can't access the camera, so this game isn't playable here.",
      );
      return undefined;
    }

    let cancelled = false;
    let stream: MediaStream | null = null;
    let rafId: number | null = null;
    let handLandmarker: HandLandmarker | null = null;
    let videoEl: HTMLVideoElement | null = null;

    async function start() {
      setStatus('requesting-camera');
      setErrorMessage(null);

      try {
        stream = await navigator.mediaDevices.getUserMedia({
          video: { width: 320, height: 240, facingMode: 'user' },
          audio: false,
        });
      } catch {
        if (!cancelled) {
          setStatus('denied');
          setErrorMessage(
            'Camera access was blocked. This game is played entirely with your hand, so please allow camera access and reload the page.',
          );
        }
        return;
      }

      if (cancelled) {
        stream.getTracks().forEach(track => track.stop());
        return;
      }

      videoEl = videoRef.current;
      if (!videoEl) return;
      videoEl.srcObject = stream;
      await videoEl.play().catch(() => {});

      setStatus('loading-model');
      try {
        const { FilesetResolver, HandLandmarker: HandLandmarkerClass } =
          await loadTasksVision();
        const filesetResolver =
          await FilesetResolver.forVisionTasks(WASM_BASE_URL);
        handLandmarker = await HandLandmarkerClass.createFromOptions(
          filesetResolver,
          {
            baseOptions: {
              modelAssetPath: HAND_LANDMARKER_MODEL_URL,
              delegate: 'GPU',
            },
            runningMode: 'VIDEO',
            numHands: 1,
          },
        );
      } catch {
        if (!cancelled) {
          setStatus('error');
          setErrorMessage(
            "Hand tracking couldn't load, so this game isn't playable right now.",
          );
        }
        return;
      }

      if (cancelled || !handLandmarker) return;
      setStatus('no-hand');
      let trackingHand = false;

      const detect = () => {
        if (cancelled || !handLandmarker || !videoRef.current) return;

        if (videoRef.current.readyState >= 2) {
          const result = handLandmarker.detectForVideo(
            videoRef.current,
            performance.now(),
          );
          const landmarks = result.landmarks?.[0];

          if (landmarks) {
            const rawY =
              (landmarks[WRIST].y + landmarks[MIDDLE_FINGER_MCP].y) / 2;
            const previous = handYRef.current ?? rawY;
            handYRef.current = previous + (rawY - previous) * SMOOTHING;
            if (!trackingHand) {
              trackingHand = true;
              setStatus('tracking');
            }
          } else {
            handYRef.current = null;
            if (trackingHand) {
              trackingHand = false;
              setStatus('no-hand');
            }
          }
        }

        rafId = requestAnimationFrame(detect);
      };

      rafId = requestAnimationFrame(detect);
    }

    start();

    return () => {
      cancelled = true;
      if (rafId !== null) cancelAnimationFrame(rafId);
      stream?.getTracks().forEach(track => track.stop());
      if (videoEl) videoEl.srcObject = null;
      handLandmarker?.close();
      handYRef.current = null;
    };
  }, [enabled]);

  return { status, videoRef, handYRef, errorMessage };
}
